import * as api from '@owl-nest/api-client/latest'
import * as base from './base'
import * as Enum from '@owl-nest/legacy-enum'

export const ExtraTimeStatus = Enum.create({
  DATE_END_IS_TOO_FAR_FUNDED: 'date_end_is_to_far_funded',
  DATE_END_IS_TOO_FAR_NOT_FUNDED: 'date_end_is_to_far_not_funded',
  EXTRA_TIME_IS_ENDED_AUTO: 'extra_time_is_ended_auto',
  EXTRA_TIME_IS_ENDED_MANUAL: 'extra_time_is_ended_manual',
  EXTRA_TIME_IS_ON: 'extra_time_is_on',
  EXTRA_TIME_NOT_AVAILABLE: 'extra_time_not_available',
  NOT_FUNDED: 'not_funded',
  ONLINE_WITH_EXTRA_TIME_NO_SAVED: 'online_with_extra_time_no_saved',
  ONLINE_WITH_EXTRA_TIME_SAVED: 'online_with_extra_time_saved',
  ONLINE_WITHOUT_EXTRA_TIME: 'online_without_extra_time',
})
export type ExtraTimeStatus = Enum.ValueOf<typeof ExtraTimeStatus>

export function isEditionBlocked(currentStatus: ExtraTimeStatus) {
  return currentStatus === ExtraTimeStatus.EXTRA_TIME_NOT_AVAILABLE
}

export function isDisabled(currentStatus: ExtraTimeStatus) {
  return (
    currentStatus === ExtraTimeStatus.EXTRA_TIME_NOT_AVAILABLE ||
    currentStatus === ExtraTimeStatus.NOT_FUNDED ||
    currentStatus === ExtraTimeStatus.DATE_END_IS_TOO_FAR_FUNDED ||
    currentStatus === ExtraTimeStatus.DATE_END_IS_TOO_FAR_NOT_FUNDED
  )
}

export function isEnded(currentStatus: ExtraTimeStatus) {
  return (
    currentStatus === ExtraTimeStatus.EXTRA_TIME_IS_ENDED_AUTO ||
    currentStatus === ExtraTimeStatus.EXTRA_TIME_IS_ENDED_MANUAL
  )
}

export function isFinishOrOn(currentStatus: ExtraTimeStatus) {
  return (
    isEnded(currentStatus) ||
    currentStatus === ExtraTimeStatus.EXTRA_TIME_NOT_AVAILABLE ||
    currentStatus === ExtraTimeStatus.EXTRA_TIME_IS_ON ||
    currentStatus === ExtraTimeStatus.ONLINE_WITH_EXTRA_TIME_SAVED
  )
}

export function isOnAndSaved(currentStatus: ExtraTimeStatus) {
  return (
    currentStatus === ExtraTimeStatus.ONLINE_WITH_EXTRA_TIME_SAVED || currentStatus === ExtraTimeStatus.EXTRA_TIME_IS_ON
  )
}

export function getExtraTimeStatus({
  extraTimeActivated,
  numberMinutesLeft,
  numberDayToEnd,
  project,
}: {
  extraTimeActivated: boolean
  numberMinutesLeft: number
  numberDayToEnd: number
  project: api.Project
}): ExtraTimeStatus {
  // Wordings and behaviour of the extratime feature are the same for projects
  // that are still being edited (and not online ye) and projects already online but not funded yet.
  // Both cases are considered as not funded.
  if (!base.isOnline(project)) {
    return ExtraTimeStatus.NOT_FUNDED
  }

  if (numberDayToEnd >= 7) {
    return project.goal_raised
      ? ExtraTimeStatus.DATE_END_IS_TOO_FAR_FUNDED
      : ExtraTimeStatus.DATE_END_IS_TOO_FAR_NOT_FUNDED
  }

  if (numberMinutesLeft <= 0 && !extraTimeActivated && !base.hadExtraTime(project)) {
    return ExtraTimeStatus.EXTRA_TIME_NOT_AVAILABLE
  }

  if (base.isExtraTime(project)) {
    return ExtraTimeStatus.EXTRA_TIME_IS_ON
  }

  // EXTRA_TIME_IS_ENDED_MANUAL and EXTRA_TIME_IS_ENDED_AUTO are set
  // if and only if time to set an extenstion is over
  // In other words: if a project has a canceled or ended extention
  // and if there is still some reminaing time to activate an extension (7 days after project end),
  // we don't use these 2 statuses.
  if (numberMinutesLeft <= 0) {
    if (project.extra_time_canceled_at !== undefined) {
      return ExtraTimeStatus.EXTRA_TIME_IS_ENDED_MANUAL
    }

    if (base.hadExtraTime(project)) {
      return ExtraTimeStatus.EXTRA_TIME_IS_ENDED_AUTO
    }
  }

  if (!project.goal_raised) {
    return ExtraTimeStatus.NOT_FUNDED
  }

  if (base.hasExtraTime(project) && extraTimeActivated) {
    return ExtraTimeStatus.ONLINE_WITH_EXTRA_TIME_SAVED
  }

  if (extraTimeActivated) {
    return ExtraTimeStatus.ONLINE_WITH_EXTRA_TIME_NO_SAVED
  } else {
    return ExtraTimeStatus.ONLINE_WITHOUT_EXTRA_TIME
  }
}
